import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Import Toastify CSS
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { register as registerUser } from '../../services/authService';
import { ChevronLeft, Eye, EyeOff } from 'lucide-react'; // Import EyeOff

// Define Yup validation schema
const schema = yup.object({
  first_name: yup.string().required('First Name is required'),
  last_name: yup.string().required('Last Name is required'),
  client_username: yup.string().required('Username is required'),
  email: yup.string().email("Invalid email format").required("Email is required"),
  password: yup.string().required('Password is required').min(8, 'Password must be at least 8 characters long'),
  confirm_password: yup.string()
    .required('Confirm Password is required')
    .oneOf([yup.ref('password'), null], 'Passwords must match'),
  contact: yup.string().required('Mobile Number is required')
    .min(10, 'Mobile number must be at least 10 characters long')
    .max(12, 'Mobile number cannot exceed 12 characters'),
}).required();

const SignUp = () => {
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  // Initialize react-hook-form with validation schema
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onBlur", // Ensures validation happens only after user leaves field
  });

  const password = watch("password"); // Watch password field for real-time validation

  const togglePasswordVisibility = () => setPasswordVisible(prev => !prev);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(prev => !prev);
  const handleGoBack = () => navigate("/"); // Navigate to the previous page

  // Submit handler
  const onSubmit = async (data) => {
    try {
      await registerUser(data);
      toast.success("Registration successful!");
      navigate('/login');
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        Object.entries(error.response.data.errors).forEach(([field, messages]) => {
          setError(field, { type: 'server', message: messages.join(', ') });
        });
      } else {
        setError('general', { type: 'server', message: 'Registration failed. Please try again.' });
      }
    }
  };

  return (
    <div className="flex items-center justify-center bg-black min-h-screen">
      <div className="w-full h-screen max-w-sm bg-white p-4 pb-5 min-w-[375px] shadow-md">
        <div className="flex flex-col">
          <span className="text-gray-700 font-bold -ml-[5px]">
            <ChevronLeft size={20} onClick={handleGoBack} />
          </span>
          <p className="text-[28px] text-black leading-[40px] mb-[5px] mt-0 font-lato font-semibold flex items-center justify-center">
            Create New Account
          </p>
          <p className="text-md font-lato text-black mb-6 flex justify-center">Unlock Your Financial Potential</p>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-6 mx-[25px]">
              <input
                placeholder="First Name"
                type="text"
                {...register('first_name')}
                className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-full px-4 py-2"
              />
              {errors.first_name && <span className="text-red-500 text-sm">{errors.first_name.message}</span>}
            </div>

            <div className="mb-6 mx-[25px]">
              <input
                placeholder="Last Name"
                type="text"
                {...register('last_name')}
                className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-full px-4 py-2"
              />
              {errors.last_name && <span className="text-red-500 text-sm">{errors.last_name.message}</span>}
            </div>

            <div className="mb-6 mx-[25px]">
              <input
                placeholder="Username"
                type="text"
                {...register('client_username')}
                className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-full px-4 py-2"
              />
              {errors.client_username && <span className="text-red-500 text-sm">{errors.client_username.message}</span>}
            </div>

            <div className="mb-6 mx-[25px]">
              <input
                placeholder="Email"
                type="email"
                {...register('email')}
                className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-full px-4 py-2"
              />
              {errors.email && <span className="text-red-500 text-sm">{errors.email.message}</span>}
            </div>

            <div className="mb-6 mx-[25px]">
              <input
                placeholder="Contact"
                type="text"
                {...register('contact')}
                className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-full px-4 py-2"
              />
              {errors.contact && <span className="text-red-500 text-sm">{errors.contact.message}</span>}
            </div>

            <div className="mb-6 mx-[25px] relative">
              <input
                placeholder="Password"
                type={passwordVisible ? "text" : "password"}
                {...register('password')}
                className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-full px-4 py-2"
              />
              <div
                className="absolute right-2 top-2 text-gray-400 cursor-pointer"
                onClick={togglePasswordVisibility}
              >
                {passwordVisible ? <EyeOff size={24} /> : <Eye size={24} />}
              </div>
              {errors.password && <span className="text-red-500 text-sm">{errors.password.message}</span>}
            </div>

            <div className="mb-6 mx-[25px] relative">
              <input
                placeholder="Confirm password"
                type={confirmPasswordVisible ? "text" : "password"}
                {...register('confirm_password', {
                  validate: (value) => value === password || "Passwords must match",
                })}
                className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-full px-4 py-2"
              />
              <div
                className="absolute right-2 top-2 text-gray-400 cursor-pointer"
                onClick={toggleConfirmPasswordVisibility}
              >
                {confirmPasswordVisible ? <EyeOff size={24} /> : <Eye size={24} />}
              </div>
              {errors.confirm_password && <span className="text-red-500 text-sm">{errors.confirm_password.message}</span>}
            </div>

            <div className="mb-6 mx-[25px]">
              <button
                type="submit"
                className="bg-[#536895] w-full border border-[#536895] rounded-full text-white text-base font-semibold py-3 px-12"
              >
                Sign Up
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer /> {/* ToastContainer for showing toast messages */}
    </div>
  );
};

export default SignUp;
